import { auth, googleProvider } from "@/lib/firebase";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  User,
  UserCredential,
} from "firebase/auth";
import { useEffect, useState } from "react";

interface AuthState {
  user: User | null;
  loading: boolean;
}

let globalAuthState: AuthState = { user: null, loading: true };
const listeners = new Set<(state: AuthState) => void>();

// Single global listener for Firebase Auth
let unsubscribe: (() => void) | null = null;
function initAuthListener() {
  if (unsubscribe) return;
  unsubscribe = onAuthStateChanged(auth, (user) => {
    globalAuthState = { user, loading: false };
    listeners.forEach((fn) => fn(globalAuthState));
  });
}

export function useAuth() {
  const [state, setState] = useState<AuthState>(globalAuthState);

  useEffect(() => {
    initAuthListener();
    listeners.add(setState);
    // Sync with current state in case listener already fired
    setState(globalAuthState);
    return () => {
      listeners.delete(setState);
    };
  }, []);

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch {
      // Popup blocked — fall back to redirect
      try {
        await signInWithRedirect(auth, googleProvider);
      } catch {
        // Redirect also failed (e.g. blocked by browser policy) — silently ignore
      }
    }
  };

  /**
   * Email + password sign-in. Throws on failure — caller is expected
   * to translate the FirebaseError code into user-facing copy via
   * `firebaseAuthErrorMessage()`.
   */
  const signInWithEmail = async (
    email: string,
    password: string,
  ): Promise<UserCredential> => {
    return signInWithEmailAndPassword(auth, email.trim(), password);
  };

  /**
   * Create a new email + password account. Sends a verification email
   * automatically on success so the user doesn't have to trigger that
   * separately; the app still lets them in (unverified) so the flow
   * doesn't stall before the email arrives.
   */
  const createAccountWithEmail = async (
    email: string,
    password: string,
  ): Promise<UserCredential> => {
    const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
    try {
      await sendEmailVerification(cred.user);
    } catch {
      // If the verification email can't be sent (rate-limited, etc.)
      // the account still exists — we'll let the caller offer a
      // "resend" action rather than rolling back the signup.
    }
    return cred;
  };

  /** Send a password-reset email. Throws on failure. */
  const sendPasswordReset = async (email: string): Promise<void> => {
    return sendPasswordResetEmail(auth, email.trim());
  };

  /** Re-send the verification email to the currently signed-in user. */
  const resendVerification = async (): Promise<void> => {
    if (!auth.currentUser) {
      throw new Error("Not signed in");
    }
    return sendEmailVerification(auth.currentUser);
  };

  const signOut = () => firebaseSignOut(auth);

  return {
    ...state,
    signInWithGoogle,
    signInWithEmail,
    createAccountWithEmail,
    sendPasswordReset,
    resendVerification,
    signOut,
  };
}

/**
 * Translate a Firebase Auth error code into a user-facing message.
 * Firebase throws typed errors with `code` set to a stable string.
 */
export function firebaseAuthErrorMessage(err: unknown): string {
  const code =
    typeof err === "object" && err && "code" in err
      ? String((err as { code: unknown }).code)
      : "";
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
      return "Wrong email or password.";
    case "auth/user-not-found":
      return "No account with that email. Try creating one.";
    case "auth/email-already-in-use":
      return "An account already exists for that email. Try signing in.";
    case "auth/weak-password":
      return "Password must be at least 6 characters.";
    case "auth/invalid-email":
      return "That doesn't look like a valid email address.";
    case "auth/too-many-requests":
      return "Too many attempts — wait a few minutes and try again.";
    case "auth/network-request-failed":
      return "Can't reach the server — check your connection.";
    case "auth/user-disabled":
      return "This account is disabled. Email hello@howisthe.surf.";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return ""; // user backed out — no banner needed
    default:
      return "Something went wrong. Try again in a moment.";
  }
}
